// Light / dark / system theme. The choice is a per-device convenience (localStorage);
// the tokens themselves switch on <html data-theme> or the OS preference.

import { useSyncExternalStore } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
const KEY = 'ui.theme';

let current: ThemePreference | undefined;
const listeners = new Set<() => void>();

function readPreference(): ThemePreference {
  try {
    const v = localStorage.getItem(KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

function getPreference(): ThemePreference {
  current ??= readPreference();
  return current;
}

/** Sets or clears `data-theme` on <html>. */
export function applyTheme(pref: ThemePreference, root: HTMLElement = document.documentElement) {
  if (pref === 'system') delete root.dataset['theme'];
  else root.dataset['theme'] = pref;
}

/** Call before first render to avoid a flash of the wrong theme. */
export function initTheme(): void {
  applyTheme(getPreference());
}

/** Applies, remembers and broadcasts a theme choice. */
export function setThemePreference(next: ThemePreference): void {
  try {
    if (next === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, next);
  } catch {
    // Storage unavailable (private mode): the choice lasts for this page only.
  }
  current = next;
  applyTheme(next);
  listeners.forEach((l) => l());
}

function subscribePreference(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The user's theme preference and a setter that applies and remembers it. */
export function useThemePreference(): [ThemePreference, (pref: ThemePreference) => void] {
  const pref = useSyncExternalStore(subscribePreference, getPreference, () => 'system' as const);
  return [pref, setThemePreference];
}

const darkQuery = () =>
  typeof window === 'undefined' || !window.matchMedia
    ? null
    : window.matchMedia('(prefers-color-scheme: dark)');

function subscribeResolved(onChange: () => void) {
  const mq = darkQuery();
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
  mq?.addEventListener('change', onChange);
  return () => {
    observer.disconnect();
    mq?.removeEventListener('change', onChange);
  };
}

function resolved(): 'light' | 'dark' {
  const explicit = document.documentElement.dataset['theme'];
  if (explicit === 'light' || explicit === 'dark') return explicit;
  return darkQuery()?.matches ? 'dark' : 'light';
}

/** The theme actually showing right now. */
export function useResolvedTheme(): 'light' | 'dark' {
  return useSyncExternalStore(subscribeResolved, resolved, () => 'light' as const);
}
